import React, { useMemo, useState } from "react";
import { Box, Button, Paper, TextField, Typography, Alert, Stack } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import api from "../Api";
import { parseSeals } from "../utils/seals";

// Generate a printable Code128 barcode sheet (separate from the pool).
export default function SealBarcodeCard() {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const count = useMemo(() => parseSeals(raw).length, [raw]);

  const generate = async () => {
    const seals = parseSeals(raw);
    if (!seals.length) { setError("Enter at least one seal number."); return; }
    if (seals.length > 5000) { setError("Too many seals for a PDF (max 5000)."); return; }
    setError(""); setBusy(true);
    try {
      const resp = await api.post("/api/seal-barcode-pdf", { seals: raw }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      let msg = "Failed to generate barcode PDF.";
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch (_) {}
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const openVoidSeal = () => {
    // Version the static PDF URL so Cloudflare cannot reuse an older orientation.
    window.open("/void-seal.pdf?v=20261025-portrait2", "_blank", "noopener,noreferrer");
  };

  return (
    <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", boxShadow: "none", height: "100%" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>🖨 Seal Barcode PDF</Typography>
      <TextField
        fullWidth multiline minRows={2} size="small"
        label="Seal numbers — range or list"
        placeholder="MP6001-MP6100  |  MP6001,MP6003"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) generate(); }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        Code128, 2 × 10 per 3″×5″ page.{count > 0 ? ` ${count} seal${count === 1 ? "" : "s"}.` : ""}
      </Typography>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      <Box sx={{ mt: 1.5 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button variant="contained" color="success" startIcon={<PictureAsPdfIcon />} disabled={busy || count === 0} onClick={generate}>
            {busy ? "Generating…" : `Generate PDF${count ? ` (${count})` : ""}`}
          </Button>
          <Button variant="outlined" color="primary" startIcon={<OpenInNewIcon />} onClick={openVoidSeal}>
            Open Void Seal
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
